import type { Cents } from "../domain/money";
import { tradeById, type TradeId } from "../domain/trade";
import type { RateSurface, RateUnit } from "../domain/rateUnit";

/**
 * ESTIMATING CALIBRATION (spec §77).
 *
 * A real renovation budget from a real house is worth more than any
 * invented rate table — but only if it is used honestly. This engine turns
 * a CALIBRATION REFERENCE (the house, its economics, its budget line by
 * line) into three derived things, and stores none of them:
 *
 *   RATES        per-unit figures reverse-engineered from lines that STATE
 *                a quantity ("Exterior doors (4) — $2,400" → $600/each),
 *                house-sqft PROXIES where the source stated the house and
 *                nothing finer, and flat ALLOWANCES for everything else.
 *                Every rate carries the provenance it came from.
 *   COMPARISON   the reference budget beside what the project's own
 *                detailed scope currently calculates, category by §74
 *                category — including the work each side has that the
 *                other does not. Nothing is reconciled by force: a
 *                difference is REPORTED, never multiplied away.
 *   BENCHMARKS   $/sq ft figures ($33.33 purchase, $45.14 rehab…) as
 *                sanity checks. A benchmark is never the estimating
 *                algorithm: the estimate is driven by scope.
 *
 * Honesty rules, permanent: the stated total and the sum of the lines are
 * both kept when they differ; a rate is derived only from a quantity the
 * source stated; unknown square footage yields null, never a division or
 * an invented area; and the explanation of a difference is DERIVED from
 * the category deltas — no model writes it.
 */

/* ------------------------------------------------------------------ *
 * Inputs
 * ------------------------------------------------------------------ */

export type RateDriver = "each" | "house-sqft" | "allowance";

/**
 * WHAT A RATE IS PER, STRUCTURALLY (spec §90-16/17).
 *
 * §77 derived every rate that stated a quantity as driver 'each' and threw
 * the unit away — "$58 / 34 sqft" and "$210 / 24 lf" became the same kind of
 * thing, so one could be multiplied by the other's quantity. A rate now
 * carries its UNIT and, for an area, the SURFACE it measures. `driver` stays
 * as the storage-compatible summary ('allowance' and 'house-sqft' are
 * genuinely dimensionless; everything else is 'each' as before), and a rate
 * with no unit is a pre-§90 row that keeps its old behaviour exactly.
 */
export interface RateDimension {
  unit: RateUnit | null;
  surface: RateSurface | null;
}

/** The unit strings a reference line may state, mapped onto rate units. */
const UNIT_FROM_LINE: Readonly<Record<string, { unit: RateUnit; surface: RateSurface | null }>> = {
  sqft: { unit: "sqft", surface: null },
  "floor-sqft": { unit: "sqft", surface: "floor" },
  "wall-sqft": { unit: "sqft", surface: "wall" },
  "ceiling-sqft": { unit: "sqft", surface: "ceiling" },
  "roof-sqft": { unit: "sqft", surface: "roof" },
  lf: { unit: "lf", surface: null },
  "linear-ft": { unit: "lf", surface: null },
  square: { unit: "square", surface: "roof" },
  squares: { unit: "square", surface: "roof" },
  each: { unit: "each", surface: null },
  system: { unit: "system", surface: null },
  "house-sqft": { unit: "house-sqft", surface: null },
};

/** The rate dimension a stated reference unit means; null when nobody stated one. */
export function rateDimensionFor(quantityUnit: string | null): RateDimension {
  if (!quantityUnit) return { unit: null, surface: null };
  const found = UNIT_FROM_LINE[quantityUnit.trim().toLowerCase()];
  return found ? { unit: found.unit, surface: found.surface } : { unit: null, surface: null };
}

export interface ReferenceLineInput {
  id: string;
  description: string;
  amountCents: Cents;
  categoryId: TradeId | "other";
  /** Stated in the source ("(4)") — never inferred. */
  quantityX100: number | null;
  quantityUnit: string | null;
  /** The kind of work, when the line maps cleanly onto one. */
  featureDefinitionId: string | null;
}

export interface CalibrationReferenceInput {
  id: string;
  label: string;
  livingAreaSqftX100: number | null;
  purchasePriceCents: Cents | null;
  purchaseClosingCents: Cents | null;
  /** The renovation total THE SOURCE STATED. */
  statedRehabCents: Cents | null;
  saleForecastCents: Cents | null;
  arvCents: Cents | null;
  lines: readonly ReferenceLineInput[];
}

/* ------------------------------------------------------------------ *
 * $/sq ft (spec §77L)
 * ------------------------------------------------------------------ */

/**
 * Cents per square foot, rounded half-up to the cent — or null when the
 * area is unknown or zero. $70,000 over 2,100 sq ft is 3333 ($33.33);
 * nothing here ever divides by an area nobody measured.
 */
export function perSqftCents(amountCents: Cents | null, sqftX100: number | null): Cents | null {
  if (amountCents === null || sqftX100 === null || sqftX100 <= 0) return null;
  return Math.round((amountCents * 100) / sqftX100);
}

export function formatPerSqft(perSqft: Cents | null): string {
  if (perSqft === null) return "—";
  return `$${(perSqft / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/sq ft`;
}

/* ------------------------------------------------------------------ *
 * Reference arithmetic (spec §77C)
 * ------------------------------------------------------------------ */

export interface ReferenceArithmetic {
  lineSumCents: Cents;
  statedCents: Cents | null;
  /** Stated − sum. Zero when they agree; never "fixed" by editing a line. */
  differenceCents: Cents | null;
  matches: boolean;
}

export function referenceArithmetic(reference: CalibrationReferenceInput): ReferenceArithmetic {
  const lineSumCents = reference.lines.reduce((sum, line) => sum + line.amountCents, 0);
  const statedCents = reference.statedRehabCents;
  const differenceCents = statedCents === null ? null : statedCents - lineSumCents;
  return { lineSumCents, statedCents, differenceCents, matches: differenceCents === 0 };
}

/* ------------------------------------------------------------------ *
 * Deriving rates (spec §77E/I)
 * ------------------------------------------------------------------ */

export interface DerivedRate {
  categoryId: TradeId | "other";
  featureDefinitionId: string | null;
  driver: RateDriver;
  /** Spec §90: what the rate is per, structurally. */
  unit: RateUnit | null;
  surface: RateSurface | null;
  /** Per unit for each/house-sqft; the whole figure for an allowance. */
  rateCents: Cents;
  /** Why this number exists — always stated, never a magic constant. */
  provenance: string;
  sourceLineId: string;
}

/**
 * Reverse-engineer rates from a reference — never totals:
 *
 *   • a line that STATES a quantity in a real unit yields a per-unit rate
 *     (4 doors / $2,400 → $600 each);
 *   • a line whose unit is 'house-sqft' yields a per-house-square-foot
 *     PROXY, and its provenance says so out loud — it is not a measured
 *     surface area;
 *   • every other line becomes a flat calibrated ALLOWANCE for its
 *     category (foundation work, site work — quantities nobody stated
 *     are never invented).
 */
export function deriveRates(reference: CalibrationReferenceInput): DerivedRate[] {
  const rates: DerivedRate[] = [];
  const money = (cents: Cents) => `$${(cents / 100).toLocaleString("en-US")}`;
  for (const line of reference.lines) {
    if (line.amountCents <= 0) continue; // a $0 line calibrates nothing
    if (line.quantityX100 !== null && line.quantityX100 > 0 && line.quantityUnit === "house-sqft") {
      rates.push({
        categoryId: line.categoryId,
        featureDefinitionId: line.featureDefinitionId,
        driver: "house-sqft",
        unit: "house-sqft",
        surface: null,
        rateCents: Math.round((line.amountCents * 100) / line.quantityX100),
        provenance: `${reference.label} calibration — ${money(line.amountCents)} / ${(line.quantityX100 / 100).toLocaleString("en-US")} house sq ft ("${line.description}"). A PROXY over the whole house, not measured surface area.`,
        sourceLineId: line.id,
      });
    } else if (line.quantityX100 !== null && line.quantityX100 > 0 && line.quantityUnit !== null) {
      // The stated unit is KEPT (spec §90): "$4,608 / 24 lf" is a rate per
      // linear foot and can never price square feet or a count.
      const dimension = rateDimensionFor(line.quantityUnit);
      rates.push({
        categoryId: line.categoryId,
        featureDefinitionId: line.featureDefinitionId,
        driver: "each",
        unit: dimension.unit,
        surface: dimension.surface,
        rateCents: Math.round((line.amountCents * 100) / line.quantityX100),
        provenance: `${reference.label} calibration — ${(line.quantityX100 / 100).toLocaleString("en-US")} ${line.quantityUnit} / ${money(line.amountCents)} reference ("${line.description}").`,
        sourceLineId: line.id,
      });
    } else {
      rates.push({
        categoryId: line.categoryId,
        featureDefinitionId: line.featureDefinitionId,
        driver: "allowance",
        unit: "allowance",
        surface: null,
        rateCents: line.amountCents,
        provenance: `${reference.label} calibration — ${money(line.amountCents)} reference allowance ("${line.description}"). No quantity was stated, so none was invented.`,
        sourceLineId: line.id,
      });
    }
  }
  return rates;
}

/* ------------------------------------------------------------------ *
 * BASE RATE + WHAT THE COMPANY ACTUALLY PAID (spec §90-21)
 * ------------------------------------------------------------------ */

export interface HistoryInput {
  /** The median unit cost of comparable finished work, cents ×100. */
  medianUnitCostCentsX100: number;
  /** How many finished jobs it came from. */
  sampleSize: number;
  lowUnitCostCentsX100?: number;
  highUnitCostCentsX100?: number;
}

export interface CalibratedRate {
  /** What to estimate with, cents per unit. */
  predictedCents: Cents;
  baseCents: Cents;
  historyCents: Cents | null;
  sampleSize: number;
  /** How much of the move from base to history was taken, percent ×100. */
  historyWeightPctX100: number;
  confidence: "none" | "limited" | "good";
  /** Arithmetic in words — the user can always check it. */
  explanation: string;
}

/** How much a company's own history is allowed to move a base rate. */
const HISTORY_WEIGHT_PCT_X100: Readonly<Record<number, number>> = { 0: 0, 1: 0, 2: 5000, 3: 5000, 4: 7500 };
const MAX_HISTORY_WEIGHT_PCT_X100 = 7500;

/**
 * BASE + HISTORY, DETERMINISTIC AND EXPLAINABLE (spec §90-21/25).
 *
 * The prediction moves PART of the way from the base rate to what finished
 * jobs actually cost — half with two or three comparable jobs, three
 * quarters with four or more, nothing with one. A single job never
 * overwrites a rate, and the median is what history contributes, so one
 * abnormal job among several cannot drag the answer (§90-36): the
 * exclusion list, not a hidden filter, is how a job stops counting at all.
 *
 * There is no model here. Same inputs, same number, and the sentence says
 * how it was reached.
 */
export function calibratedRate(baseCents: Cents, history: HistoryInput | null): CalibratedRate {
  // FOUND IN ACCEPTANCE: this sentence printed "225.00" with no currency,
  // so a rate read as a bare number in the one place that explains it.
  const money = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (!history || history.sampleSize <= 0) {
    return {
      predictedCents: baseCents,
      baseCents,
      historyCents: null,
      sampleSize: 0,
      historyWeightPctX100: 0,
      confidence: "none",
      explanation: `${money(baseCents)} from your base rate. No comparable finished work yet, so nothing has moved it.`,
    };
  }
  const historyCents = Math.round(history.medianUnitCostCentsX100 / 100);
  const weight = HISTORY_WEIGHT_PCT_X100[history.sampleSize] ?? MAX_HISTORY_WEIGHT_PCT_X100;
  const predictedCents = baseCents + Math.round(((historyCents - baseCents) * weight) / 10000);
  const jobs = `${history.sampleSize} finished job${history.sampleSize === 1 ? "" : "s"}`;
  return {
    predictedCents,
    baseCents,
    historyCents,
    sampleSize: history.sampleSize,
    historyWeightPctX100: weight,
    confidence: weight === 0 ? "none" : weight >= MAX_HISTORY_WEIGHT_PCT_X100 ? "good" : "limited",
    explanation:
      weight === 0
        ? `${money(baseCents)} from your base rate. ${jobs} is not enough comparable history to move it yet (two are needed).`
        : `${money(predictedCents)}: your base rate ${money(baseCents)} moved ${weight / 100}% of the way to ${money(historyCents)}, the median of ${jobs}.`,
  };
}

/* ------------------------------------------------------------------ *
 * Comparison (spec §77G/M/N)
 * ------------------------------------------------------------------ */

export interface CategoryCalculation {
  categoryId: string;
  displayName: string;
  calculatedCents: Cents;
  featureCount: number;
}

export interface CategoryComparison {
  categoryId: string;
  displayName: string;
  referenceCents: Cents;
  calculatedCents: Cents;
  /** calculated − reference. */
  differenceCents: Cents;
  /** True when the project has scope in this category. */
  inScope: boolean;
  /** True when the reference budget carries money for it. */
  inReference: boolean;
}

export interface CalibrationComparison {
  rows: CategoryComparison[];
  referenceTotalCents: Cents;
  calculatedTotalCents: Cents;
  differenceCents: Cents;
  /** Reference work with no scope in the project (§77N) — surfaced for the user to decide; nothing is created. */
  referenceOnly: CategoryComparison[];
  /** Project scope the reference budget never priced. */
  scopeOnly: CategoryComparison[];
  /** Deterministic, derived from the deltas — no model writes these. */
  explanations: string[];
}

const labelFor = (categoryId: string): string =>
  tradeById(categoryId)?.displayName ?? (categoryId === "other" ? "Other / professional" : categoryId);

export function compareToReference(
  referenceLines: readonly ReferenceLineInput[],
  calculated: readonly CategoryCalculation[],
): CalibrationComparison {
  const referenceByCategory = new Map<string, Cents>();
  for (const line of referenceLines) {
    referenceByCategory.set(line.categoryId, (referenceByCategory.get(line.categoryId) ?? 0) + line.amountCents);
  }
  const calculatedByCategory = new Map(calculated.map((c) => [c.categoryId, c]));

  const ids = [...new Set([...referenceByCategory.keys(), ...calculated.map((c) => c.categoryId)])];
  const rows: CategoryComparison[] = ids
    .map((categoryId) => {
      const referenceCents = referenceByCategory.get(categoryId) ?? 0;
      const calc = calculatedByCategory.get(categoryId);
      const calculatedCents = calc?.calculatedCents ?? 0;
      return {
        categoryId,
        displayName: calc?.displayName ?? labelFor(categoryId),
        referenceCents,
        calculatedCents,
        differenceCents: calculatedCents - referenceCents,
        inScope: (calc?.featureCount ?? 0) > 0 || calculatedCents !== 0,
        inReference: referenceCents !== 0,
      };
    })
    .sort(
      (a, b) => Math.abs(b.differenceCents) - Math.abs(a.differenceCents) || a.displayName.localeCompare(b.displayName),
    );

  const referenceTotalCents = rows.reduce((s, r) => s + r.referenceCents, 0);
  const calculatedTotalCents = rows.reduce((s, r) => s + r.calculatedCents, 0);
  const referenceOnly = rows.filter((r) => r.inReference && !r.inScope);
  const scopeOnly = rows.filter((r) => r.inScope && !r.inReference);

  /* The explanation is arithmetic wearing words (spec §77M): the largest
   * absent categories first, then the largest priced differences. */
  const money = (cents: Cents) => `$${Math.round(Math.abs(cents) / 100).toLocaleString("en-US")}`;
  const explanations: string[] = [];
  if (referenceOnly.length > 0) {
    const total = referenceOnly.reduce((s, r) => s + r.referenceCents, 0);
    const names = referenceOnly.slice(0, 4).map((r) => r.displayName);
    explanations.push(
      `${money(total)} of the reference budget has no scope in this project (${names.join(", ")}${referenceOnly.length > 4 ? "…" : ""}). Nothing was created to close that gap — adding the scope is the user's decision.`,
    );
  }
  if (scopeOnly.length > 0) {
    const total = scopeOnly.reduce((s, r) => s + r.calculatedCents, 0);
    explanations.push(
      `${money(total)} of this project's scope has no counterpart in the reference (${scopeOnly
        .slice(0, 4)
        .map((r) => r.displayName)
        .join(", ")}).`,
    );
  }
  for (const row of rows.filter((r) => r.inScope && r.inReference && Math.abs(r.differenceCents) >= 50_000).slice(0, 4)) {
    explanations.push(
      `${row.displayName}: reference ${money(row.referenceCents)}, calculated ${money(row.calculatedCents)} (${row.differenceCents > 0 ? "+" : "−"}${money(row.differenceCents)}).`,
    );
  }

  return {
    rows,
    referenceTotalCents,
    calculatedTotalCents,
    differenceCents: calculatedTotalCents - referenceTotalCents,
    referenceOnly,
    scopeOnly,
    explanations,
  };
}

/* ------------------------------------------------------------------ *
 * Project benchmarks (spec §77L)
 * ------------------------------------------------------------------ */

export interface ProjectSqftMetrics {
  livingAreaSqftX100: number | null;
  purchasePerSqft: Cents | null;
  renovationPerSqft: Cents | null;
  /** ARV for a flip; estimated property value for a rental — the CALLER labels it in the strategy's own vocabulary. */
  valuePerSqft: Cents | null;
  salePerSqft: Cents | null;
}

export function projectSqftMetrics(input: {
  livingAreaSqftX100: number | null;
  purchasePriceCents: Cents | null;
  renovationBasisCents: Cents | null;
  expectedValueCents: Cents | null;
  saleForecastCents: Cents | null;
  strategy: "flip" | "rental";
}): ProjectSqftMetrics {
  const area = input.livingAreaSqftX100;
  return {
    livingAreaSqftX100: area,
    purchasePerSqft: perSqftCents(input.purchasePriceCents, area),
    renovationPerSqft: perSqftCents(input.renovationBasisCents, area),
    valuePerSqft: perSqftCents(input.expectedValueCents, area),
    // A rental has no sale forecast: the concept is absent, not zero.
    salePerSqft: input.strategy === "flip" ? perSqftCents(input.saleForecastCents, area) : null,
  };
}
